/**
 * Authenticated calls to the POS API (D-INV-3).
 *
 * Invoicing routes carry customer data, so on top of the shared x-api-key
 * (posApi.ts) they need the signed-in user's Cognito ID token and the shop
 * the user is acting for. API Gateway's Cognito authorizer reads the raw
 * token from `Authorization` (no "Bearer" prefix); the Lambda then checks the
 * user may act for `X-Netlise-Shop-Id`.
 *
 * Errors come back as InvoiceApiError with the server's stable `code` and
 * per-field problems (CONTRACT.md §1.1), so screens can show the right
 * message next to the right field instead of a generic toast.
 */
import { Auth } from 'aws-amplify';
import { POS_API_KEY, POS_API_URL } from './posApi';

export class InvoiceApiError extends Error {
  code: string;
  status: number;
  fields: Record<string, string>;
  details: any;

  constructor(code: string, status: number, message: string, details: any = null) {
    super(message);
    this.name = 'InvoiceApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.fields = details?.fields ?? {};
  }
}

async function idToken(): Promise<string | null> {
  try {
    return (await Auth.currentSession()).getIdToken().getJwtToken();
  } catch (e) {
    return null;
  }
}

/** Turn a non-2xx response into an InvoiceApiError. Exported for tests. */
export function toApiError(status: number, text: string): InvoiceApiError {
  let body: any = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch (e) {
    // A gateway timeout or a 502 returns HTML.
    return new InvoiceApiError('unavailable', status, 'Invoicing is not responding. Try again in a moment.');
  }
  if (status === 401 || status === 403 && !body?.code) {
    // Answered by API Gateway itself: expired session or missing key.
    return new InvoiceApiError(status === 401 ? 'unauthorized' : 'forbidden', status, status === 401 ? 'Your session has expired. Sign in again.' : 'You do not have access to this.');
  }
  return new InvoiceApiError(body?.code || 'error', status, body?.message || `Invoicing error (${status})`, body?.details ?? null);
}

export interface AuthedInit {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  idempotencyKey?: string;
}

export function buildQuery(query?: AuthedInit['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  });
  const s = params.toString();
  return s ? `?${s}` : '';
}

export async function authedPosApi<T = any>(shopId: string, path: string, init: AuthedInit = {}): Promise<T> {
  if (!shopId) throw new InvoiceApiError('shop_id_required', 400, 'No shop selected');
  const token = await idToken();
  if (!token) throw new InvoiceApiError('unauthorized', 401, 'Your session has expired. Sign in again.');

  let res: Response;
  try {
    res = await fetch(`${POS_API_URL}${path}${buildQuery(init.query)}`, {
      method: init.method ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': POS_API_KEY,
        Authorization: token,
        'X-Netlise-Shop-Id': shopId,
        ...(init.idempotencyKey ? { 'Idempotency-Key': init.idempotencyKey } : {}),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch (e) {
    throw new InvoiceApiError('network', 0, 'Could not reach Netlise. Check your connection.');
  }

  const text = await res.text();
  if (!res.ok) throw toApiError(res.status, text);
  return (text ? JSON.parse(text) : null) as T;
}
